import { ExternalProvider } from '@ethersproject/providers'
import { ChainId, connectorLocalStorageKey, ConnectorName } from '@mprolab/sdk'
import { BASE_SCAN_URLS } from 'config/constants'
import { metamaskMobileStore } from 'mobxStores/MetamaskMobile'
import { getRpcUrlByChainId } from './getRpcUrl'

export const NETWORK_CONFIG = {
  [ChainId.BSC_MAINNET]: {
    name: 'BNB Smart Chain Mainnet',
    scanURL: BASE_SCAN_URLS[ChainId.BSC_MAINNET],
    currency: {
      name: 'BSC',
      symbol: 'BNB',
      decimals: 18
    }
  },
  [ChainId.BSC_TESTNET]: {
    name: 'BNB Smart Chain Testnet',
    scanURL: BASE_SCAN_URLS[ChainId.BSC_MAINNET],
    currency: {
      name: 'tBNB',
      symbol: 'tBNB',
      decimals: 18
    }
  },
  [ChainId.MATIC_MAINNET]: {
    name: 'Polygon Mainnet',
    scanURL: BASE_SCAN_URLS[ChainId.MATIC_MAINNET],
    currency: {
      name: 'POL',
      symbol: 'POL',
      decimals: 18
    }
  },
  [ChainId.MATIC_TESTNET]: {
    name: 'Polygon Testnet',
    scanURL: BASE_SCAN_URLS[ChainId.MATIC_TESTNET],
    currency: {
      name: 'POL',
      symbol: 'POL',
      decimals: 18
    }
  },
  [ChainId.ETH_MAINNET]: {
    name: 'Ethereum Mainnet',
    scanURL: BASE_SCAN_URLS[ChainId.ETH_MAINNET],
    currency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    }
  },
  [ChainId.ETH_GOERLI]: {
    name: 'Ethereum Testnet',
    scanURL: BASE_SCAN_URLS[ChainId.ETH_GOERLI],
    currency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    }
  },
  [ChainId.ETH_SEPOLIA]: {
    name: 'Sepolia Testnet',
    scanURL: BASE_SCAN_URLS[ChainId.ETH_SEPOLIA],
    currency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    }
  },
  [ChainId.ARBITRUM_MAINNET]: {
    name: 'Arbitrum Mainnet',
    scanURL: BASE_SCAN_URLS[ChainId.ARBITRUM_MAINNET],
    currency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    }
  },
  [ChainId.ARBITRUM_TESTNET]: {
    name: 'Arbitrum Testnet',
    scanURL: BASE_SCAN_URLS[ChainId.ARBITRUM_TESTNET],
    currency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    }
  },
  [ChainId.BASE_MAINNET]: {
    name: 'Base Mainnet',
    scanURL: BASE_SCAN_URLS[ChainId.BASE_MAINNET],
    currency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    }
  },
  [ChainId.BASE_TESTNET]: {
    name: 'Base Testnet',
    scanURL: BASE_SCAN_URLS[ChainId.BASE_TESTNET],
    currency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18
    }
  }
}

export const waitForAccount = async (externalProvider?: ExternalProvider, timeout?: number) => {
  const provider = externalProvider || window.ethereum || window.trustwallet
  if (!provider) return Promise.reject(new Error('Provider is undefined'))

  return new Promise<void>((resolve, reject) => {
    let timer: NodeJS.Timeout | null = null

    const interval = setInterval(async () => {
      const accounts = await provider.request({ method: 'eth_accounts' })
      if (accounts.length > 0) {
        clearInterval(interval)
        if (timer) clearTimeout(timer) // Clear the timeout if an account is found
        resolve()
      }
    }, 1000)

    if (timeout)
      timer = setTimeout(() => {
        clearInterval(interval)
        reject(new Error('Timeout: No account found'))
      }, timeout)
  })
}

export enum SetupNetworkResponse {
  REQUEST_SUCCESS,
  REQUEST_REJECTED,
  REQUEST_PENDING,
  REQUEST_FAILED,
  ETHEREUM_UNDEFINED,
  METHOD_NOT_FOUND
}

/**
 * Prompt the user to add BSC as a network on Metamask, or switch to BSC if the wallet is on a different network
 * @returns {SetupNetworkResponse} true if the setup succeeded, false otherwise
 */
export const setupNetwork = async (
  chainId: number,
  externalProvider?: ExternalProvider
): Promise<SetupNetworkResponse> => {
  const provider = externalProvider || window.ethereum || window.trustwallet
  if (provider) {
    try {
      // @ts-ignore
      if (provider?.session?.peer?.metadata?.redirect?.native === 'metamask://') {
        metamaskMobileStore.setLoading(true)
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: `0x${chainId.toString(16)}`,
              chainName: NETWORK_CONFIG[chainId].name,
              nativeCurrency: {
                name: NETWORK_CONFIG[chainId].currency.name,
                symbol: NETWORK_CONFIG[chainId].currency.symbol,
                decimals: NETWORK_CONFIG[chainId].currency.decimals
              },
              rpcUrls: [getRpcUrlByChainId(chainId)],
              blockExplorerUrls: [`${NETWORK_CONFIG[chainId].scanURL}/`]
            }
          ]
        })
      } else {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${chainId.toString(16)}` }]
        })
      }

      await waitForAccount(provider)
      return SetupNetworkResponse.REQUEST_SUCCESS
    } catch (switchError) {
      if ((switchError as any)?.code === -32000) {
        // We need to handle this case to force user to monual network switch
        return SetupNetworkResponse.METHOD_NOT_FOUND
      }
      if ((switchError as any)?.code === -32002) {
        return SetupNetworkResponse.REQUEST_PENDING
      }
      if ((switchError as any)?.code === 4001 || (switchError as any)?.code === 5000) {
        metamaskMobileStore.setLoading(false)
        return SetupNetworkResponse.REQUEST_REJECTED
      }
      if ((switchError as any)?.code === 4902) {
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: `0x${chainId.toString(16)}`,
                chainName: NETWORK_CONFIG[chainId].name,
                nativeCurrency: {
                  name: NETWORK_CONFIG[chainId].currency.name,
                  symbol: NETWORK_CONFIG[chainId].currency.symbol,
                  decimals: NETWORK_CONFIG[chainId].currency.decimals
                },
                rpcUrls: [getRpcUrlByChainId(chainId)],
                blockExplorerUrls: [`${NETWORK_CONFIG[chainId].scanURL}/`]
              }
            ]
          })
          await waitForAccount(provider)
          return SetupNetworkResponse.REQUEST_SUCCESS
        } catch (error) {
          console.error('Failed to setup the network in Metamask:', error)
          return SetupNetworkResponse.REQUEST_FAILED
        }
      }
      return SetupNetworkResponse.REQUEST_FAILED
    }
  } else {
    console.error("Can't setup the network on metamask because window.ethereum is undefined")
    return SetupNetworkResponse.ETHEREUM_UNDEFINED
  }
}
